import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useCognitoAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/hooks/useOrganization';
import { api } from '@/lib/api';
import type { BeangoError } from '@/lib/beango-error';
import { ErrorCode } from '@/lib/error-codes';

interface InvitationDetail {
  id: number;
  organization_id: number;
  organization_name: string;
  role: string;
  invited_email: string;
  invited_by_name: string;
  invited_at: string;
  invitation_token: string;
  invitation_expires_at: string;
  status: string;
}

type PageState =
  | { type: 'loading' }
  | { type: 'not-logged-in'; invitation: InvitationDetail }
  | { type: 'ready-to-accept'; invitation: InvitationDetail }
  | { type: 'error'; code: number; message: string }
  | { type: 'accepted'; orgId: number; orgName: string; role: string };

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function formatExpiry(dateStr: string): string {
  const diff = new Date(dateStr).getTime() - Date.now();
  if (diff <= 0) return 'Expired';
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days > 0) return `${days} day${days > 1 ? 's' : ''} remaining`;
  const hours = Math.floor(diff / (1000 * 60 * 60));
  return `${hours} hour${hours > 1 ? 's' : ''} remaining`;
}

export function AcceptInvitation() {
  const { token } = useParams<{ token: string }>();
  const { user, loading: authLoading, idToken } = useCognitoAuth();
  const { refreshOrganizations } = useOrganization();
  const navigate = useNavigate();

  const [state, setState] = useState<PageState>({ type: 'loading' });
  const [accepting, setAccepting] = useState(false);

  const fetchInvitation = useCallback(async () => {
    if (!token || !idToken) return;
    try {
      const data = await api.get<InvitationDetail>(`/api/invitations/${token}`, { errorCode: ErrorCode.FETCH_INVITATION, token: idToken });
      if (!authLoading && !user) {
        setState({ type: 'not-logged-in', invitation: data });
      } else {
        setState({ type: 'ready-to-accept', invitation: data });
      }
    } catch (err) {
      const beStatus = (err instanceof Error && 'code' in err) ? (err as BeangoError).status : 0;
      if (beStatus === 404) {
        setState({ type: 'error', code: 404, message: 'This invitation was not found. It may have been revoked or already accepted.' });
      } else if (beStatus === 410) {
        setState({ type: 'error', code: 410, message: 'This invitation has expired. Please request a new invitation from the organization admin.' });
      } else {
        setState({ type: 'error', code: 0, message: 'Failed to load invitation. Please try again.' });
      }
    }
  }, [token, user, authLoading, idToken]);

  useEffect(() => {
    if (!authLoading) {
      void fetchInvitation();
    }
  }, [fetchInvitation, authLoading]);

  const handleAccept = useCallback(async () => {
    if (!token || !idToken) return;
    setAccepting(true);
    try {
      const result = await api.post<{ organization_id: number; organization_name: string; role: string }>(`/api/invitations/${token}/accept`, {}, { errorCode: ErrorCode.ACCEPT_INVITATION, token: idToken });

      await refreshOrganizations();
      const orgId = result.organization_id;
      localStorage.setItem('currentOrganizationId', String(orgId));

      void navigate(`/app/organizations/${orgId}/settings/members`, { replace: true });
    } catch (err) {
      const beStatus = (err instanceof Error && 'code' in err) ? (err as BeangoError).status : 0;
      if (beStatus === 409) {
        setState({ type: 'error', code: 409, message: 'You already belong to another organization.' });
      } else if (beStatus === 422) {
        setState({ type: 'error', code: 422, message: 'This invitation has already been accepted.' });
      } else {
        setState({ type: 'error', code: 0, message: err instanceof Error ? err.message : 'Failed to accept invitation' });
      }
    } finally {
      setAccepting(false);
    }
  }, [token, refreshOrganizations, navigate, idToken]);

  if (authLoading || state.type === 'loading') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center">
          <div className="animate-spin text-4xl mb-4">🌿</div>
          <p className="text-muted-foreground">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (state.type === 'error') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
        <div className="max-w-lg w-full">
          <div className="text-center mb-8">
            <i className={`bi ${state.code === 410 ? 'bi-clock-history' : 'bi-exclamation-circle'} text-5xl text-muted-foreground mb-4 block`}></i>
            <h1 className="text-2xl font-bold text-foreground">
              {state.code === 410 ? 'Invitation Expired' : state.code === 404 ? 'Invitation Not Found' : 'Invitation Problem'}
            </h1>
            <p className="text-muted-foreground mt-2">{state.message}</p>
          </div>

          <div className="bg-card rounded-lg border border-border p-6 text-center">
            {state.code === 410 && (
              <p className="text-sm text-muted-foreground mb-4">
                Invitations are valid for 7 days. Your organization admin can send you a new invitation.
              </p>
            )}
            <div className="flex flex-col gap-3">
              {user ? (
                <Link to="/app">
                  <Button variant="default">Go to Dashboard</Button>
                </Link>
              ) : (
                <Link to="/login">
                  <Button variant="default">Sign In</Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const invitation: InvitationDetail = (state as { invitation: InvitationDetail }).invitation;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
      <div className="max-w-lg w-full">
        <div className="text-center mb-8">
          <i className="bi bi-envelope-heart text-5xl text-primary block mb-3"></i>
          <h1 className="text-2xl font-bold text-foreground">
            You're Invited
          </h1>
          <p className="text-muted-foreground mt-1">
            {invitation.invited_by_name
              ? `${invitation.invited_by_name} has invited you to join`
              : 'You have been invited to join'}
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{invitation.organization_name}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 mb-6">
              <div className="flex justify-between items-center py-2 border-b last:border-0">
                <span className="text-sm text-muted-foreground">Email</span>
                <span className="text-sm font-medium">{invitation.invited_email}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b last:border-0">
                <span className="text-sm text-muted-foreground">Role</span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
                  invitation.role === 'owner'
                    ? 'bg-red-100 text-red-700 border-red-200'
                    : invitation.role === 'admin'
                    ? 'bg-blue-100 text-blue-700 border-blue-200'
                    : 'bg-gray-100 text-gray-700 border-gray-200'
                }`}>
                  {invitation.role}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b last:border-0">
                <span className="text-sm text-muted-foreground">Expires</span>
                <div className="text-right">
                  <span className="text-sm font-medium">{formatExpiry(invitation.invitation_expires_at)}</span>
                  <div className="text-xs text-muted-foreground">{formatDate(invitation.invitation_expires_at)}</div>
                </div>
              </div>
            </div>

            {state.type === 'ready-to-accept' ? (
              <Button
                variant="default"
                className="w-full"
                onClick={() => void handleAccept()}
                disabled={accepting}
              >
                {accepting ? 'Accepting...' : 'Accept Invitation'}
              </Button>
            ) : (
              <div className="text-center space-y-3">
                <p className="text-sm text-muted-foreground">
                  Sign in to accept this invitation.
                </p>
                <Link to={`/login?invite_token=${token}`}>
                  <Button variant="default" size="sm" className="w-full">
                    <i className="bi bi-box-arrow-in-right mr-1"></i> Sign in to Accept
                  </Button>
                </Link>
                <p className="text-xs text-muted-foreground">
                  Don't have an account?{' '}
                  <Link to={`/register?invite_token=${token}`} className="text-primary hover:underline">
                    Create one
                  </Link>
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
